/**
 * VIN Pulse OS Ecommerce – HTTP client.
 *
 * Singleton axios wrapper for all backend (vin-pulse-os-api) calls.
 * Mirrors the structure of vin-pulse-os-web/src/services/api.ts so the
 * Customer-facing ecommerce app speaks the same contracts:
 *   - Bearer JWT in `Authorization`
 *   - Tokens persisted to localStorage
 *   - 401 → silent refresh via /token/refresh, then retry the original request
 *   - Backend envelope `{ success, data }` is unwrapped; legacy `{ error: { code, message } }`
 *     bodies are normalized into thrown `Error(message)`.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const ACCESS_TOKEN_KEY = 'vinpulse_ecom_access_token';
const REFRESH_TOKEN_KEY = 'vinpulse_ecom_refresh_token';

interface ApiErrorBody {
  code: string;
  message: string;
  details?: Record<string, any>;
}

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.loadTokensFromStorage();

    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          this.refreshToken
        ) {
          originalRequest._retry = true;

          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.client(originalRequest));
              });
            });
          }

          this.isRefreshing = true;
          try {
            const newAccessToken = await this.performRefresh();
            this.isRefreshing = false;
            this.flushQueue(newAccessToken);
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return this.client(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            this.clearTokens();
            throw refreshError;
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private loadTokensFromStorage(): void {
    try {
      this.accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      this.refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      // SSR / private mode – ignore
    }
  }

  private saveTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    } catch {
      // ignore
    }
  }

  clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      // ignore
    }
  }

  setTokens(accessToken: string, refreshToken: string): void {
    this.saveTokens(accessToken, refreshToken);
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private flushQueue(token: string): void {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }

  private async performRefresh(): Promise<string> {
    if (!this.refreshToken) throw new Error('No refresh token available');

    // Use bare axios (no interceptor) to avoid an infinite loop on 401.
    const response = await axios.post<any>(`${API_BASE_URL}/token/refresh`, {
      refreshToken: this.refreshToken,
    });
    const data = response.data?.data ?? response.data;
    if (!data?.accessToken || !data?.refreshToken) {
      throw new Error('Token refresh failed');
    }
    this.saveTokens(data.accessToken, data.refreshToken);
    return data.accessToken as string;
  }

  /** Generic helpers.
   *  The backend uses two response shapes:
   *    1. Explicit envelope: `{ success: true, data: <payload> }` (e.g. customer-self order endpoints)
   *    2. Raw payload, which may itself contain a top-level `data` field
   *       (e.g. `/skus` returns `{ data: [...], pagination: {...} }`).
   *  We must only unwrap on style 1, otherwise style-2 endpoints lose their
   *  pagination metadata. */
  private unwrapBody<T>(body: any): T {
    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      body.success === true &&
      'data' in body
    ) {
      return body.data as T;
    }
    return body as T;
  }

  async get<T>(path: string, params?: Record<string, any>): Promise<T> {
    try {
      const response = await this.client.get<any>(path, { params });
      return this.unwrapBody<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post<T>(path: string, body?: any): Promise<T> {
    try {
      const response = await this.client.post<any>(path, body);
      return this.unwrapBody<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async patch<T>(path: string, body?: any): Promise<T> {
    try {
      const response = await this.client.patch<any>(path, body);
      return this.unwrapBody<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async put<T>(path: string, body?: any): Promise<T> {
    try {
      const response = await this.client.put<any>(path, body);
      return this.unwrapBody<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async delete<T = void>(path: string): Promise<T> {
    try {
      const response = await this.client.delete<any>(path);
      return this.unwrapBody<T>(response.data);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fetch a binary response (e.g. a server-generated PDF) as a Blob, for
   * callers to turn into a download via `triggerBlobDownload`
   * (src/utils/downloadBlob.ts). Mirrors vin-pulse-os-web's apiClient.
   */
  async downloadBlob(path: string, params?: Record<string, any>): Promise<Blob> {
    try {
      const response = await this.client.get<Blob>(path, { params, responseType: 'blob' });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const body = error.response?.data;
      const apiError: ApiErrorBody | undefined =
        body?.error && typeof body.error === 'object'
          ? (body.error as ApiErrorBody)
          : (body as ApiErrorBody | undefined);

      if (apiError?.message) {
        const wrapped = new Error(apiError.message) as Error & {
          code?: string;
          status?: number;
          details?: Record<string, any>;
        };
        wrapped.code = apiError.code;
        wrapped.status = error.response?.status;
        wrapped.details = apiError.details;
        return wrapped;
      }

      const status = error.response?.status;
      if (status === 401) return new Error('Unauthorized – please login again');
      if (status === 403) return new Error('You do not have permission to perform this action');
      if (status === 404) return new Error('Resource not found');
      if (status === 422) return new Error('Invalid input – please check your data');
      return new Error(error.message || 'A network error occurred');
    }
    return error instanceof Error ? error : new Error('An unknown error occurred');
  }
}

export const apiClient = new ApiClient();
export default apiClient;
